import React from 'react';
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';

const Documentation: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Logo size="md" />

            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-slate-900 to-slate-950 px-8 py-6">
            <h1 className="text-3xl font-bold text-white">AWS API Key Setup Guide</h1>
            <p className="mt-2 text-gray-300">Create read-only AWS credentials for x-aws.com</p>
          </div>

          <div className="p-8 space-y-8">
            {/* CloudFormation Quick Setup */}
            <section>
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg p-6 text-white shadow-lg">
                <h2 className="text-2xl font-bold mb-3 flex items-center">
                  <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  NEW: One-Click AWS Setup
                </h2>
                <p className="mb-4 text-indigo-100">
                  Set up your AWS credentials in under 60 seconds using our automated CloudFormation template.
                </p>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-4">
                  <h3 className="font-semibold mb-2">How it works:</h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Click the button below to open AWS CloudFormation</li>
                    <li>Review and click "Create Stack" (no changes needed)</li>
                    <li>Wait 30 seconds for completion</li>
                    <li>Copy your credentials from the Outputs tab</li>
                  </ol>
                </div>
                <a
                  href={`https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${encodeURIComponent('https://s3.amazonaws.com/x-aws/exitaws-readonly-setup.yaml')}&stackName=ExitAWS-ReadOnly-Setup`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 transition-colors shadow-md"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Launch Quick Setup
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
                </a>
                <p className="mt-3 text-xs text-indigo-100">
                  This creates an IAM user with read-only permissions. You can delete it anytime by deleting the CloudFormation stack.
                </p>
              </div>
            </section>

            {/* Manual Setup Option */}
            <section>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
                  <svg className="h-6 w-6 mr-2 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Alternative: Manual Setup
                </h2>
                <p className="text-green-800 dark:text-green-200 mb-4">
                  If you prefer to set up permissions manually or need custom configurations:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-green-700 dark:text-green-300">
                  <li>In IAM, create a new user or select an existing user</li>
                  <li>Attach the AWS managed policy: <code className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded font-mono text-sm">ReadOnlyAccess</code></li>
                  <li>Create access keys for programmatic access</li>
                </ol>
              </div>
            </section>

            {/* Detailed Setup Steps */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <svg className="h-6 w-6 mr-2 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Step-by-Step Instructions
              </h2>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      1
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Log into AWS Console</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Navigate to your{' '}
                      <a href="https://console.aws.amazon.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center">
                        AWS Console
                        <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                      </a>{' '}
                      and sign in with your account credentials.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      2
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Navigate to IAM</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Search for "IAM" in the AWS services search bar or find it under "Security, Identity, & Compliance" in the services menu.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      3
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Create a New User</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Click on "Users" in the left sidebar, then click the "Add users" button. Enter a descriptive username like "xaws-readonly".
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      4
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Set Permissions</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Select "Attach policies directly" and search for "ReadOnlyAccess". Check the box next to this AWS managed policy.
                    </p>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      5
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Review and Create</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Review your settings and click "Create user". The user will be created with the ReadOnlyAccess policy attached.
                    </p>
                  </div>
                </div>

                {/* Step 6 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      6
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Create Access Keys</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Click on the newly created user, go to the "Security credentials" tab, and click "Create access key". Select "Application running outside AWS" as the use case.
                    </p>
                  </div>
                </div>

                {/* Step 7 */}
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-semibold">
                      7
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Save Your Credentials</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      Copy your Access Key ID and Secret Access Key. You'll need these to add to x-aws.com. Make sure to save them securely as you won't be able to view the secret key again.
                    </p>
                    <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                      <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start">
                        <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span><strong>Important:</strong> Store these credentials securely. Never share them or commit them to version control.</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Security Best Practices */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Security Best Practices</h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
                <ul className="space-y-3 text-blue-800 dark:text-blue-200">
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Use read-only permissions - x-aws.com never needs write access</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Rotate access keys regularly (every 90 days recommended)</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Store credentials securely and never share them</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Monitor access key usage in AWS CloudTrail</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Delete unused access keys promptly</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Next Steps */}
            <section>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Ready to Continue?</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Once you have your AWS credentials, return to the dashboard to add them to x-aws.com.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Back to Dashboard
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Documentation;